import {
  addMonths,
  averageDailySpending,
  bankDayShift,
  baseAmount,
  budgetStatus,
  classifyTransaction,
  computeNetWorth,
  detectRecurring,
  investmentValueOn,
  endOfMonth,
  learnCategories,
  loanStartDate,
  loanStatus,
  monthsFromTo,
  netWorthHistory,
  partOf,
  periodContaining,
  previousPeriod,
  scheduledLoanBalances,
  summarizeCashFlow,
  toBase,
  todayIn,
  UNASSIGNED,
  valuedAssetOn,
  withoutReference,
  type BankDayShift,
  type BudgetStatus,
  type CashFlowSummary,
  type CategorySuggestion,
  type LoanStatus,
  type NetWorth,
  type NetWorthPoint,
  type Period,
  type RecurringKind,
  type RecurringPattern,
} from '@fd/finance-core';
import type { ExchangeRate } from '@fd/money';
import {
  toAccountInfo,
  toEngineIncome,
  toEngineRule,
  toEngineTransaction,
  toLedgerAccount,
  toLedgerTransaction,
  toCategorized,
  toLoanTerms,
  toRecurringTransaction,
  toSettlementRecord,
  type Account,
  type Budget,
  type Category,
  type CategoryGroup,
  type EntityType,
  type Household,
  type IncomePeriod,
  type InvestmentEvent,
  type Person,
  type RecurringPattern as RecurringDecision,
  type Settlement,
  type SettlementRule,
  type Transaction,
} from '@fd/schemas';
import { computeSettlement, type SettlementContext, type SettlementResult } from '@fd/settlement-engine';
import type { EntitySyncState } from '@fd/sync-engine';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { withGroupColors, withGroupShades } from './category-colors.ts';
import { netWorthDatePlan } from './history-dates.ts';
import { callServer } from './server.ts';
import { useSession } from './session.tsx';

export type Synced<T> = T & { syncState: EntitySyncState };

/** Live list of one entity type from the local cache; refreshes on every local or synced change. */
export function useEntities<T>(type: EntityType, options: { from?: string; to?: string; limit?: number; history?: boolean } = {}) {
  const { engine, householdId } = useSession();
  // Transactions start at the household's start day (D-040): what came before counts and shows
  // nowhere. Only finding recurring payments reads the history (`history`), as the owner chose
  // after the recurring page went empty with a single month to look at.
  const countFrom = useCountFrom();
  const { history, ...range } = options;
  const scoped = type === 'transaction' && !history && countFrom && (!range.from || range.from < countFrom) ? { ...range, from: countFrom } : range;
  return useQuery({
    queryKey: ['entities', type, householdId, scoped, history === true],
    queryFn: async () => {
      if (!householdId) return [];
      const rows = (await engine.list(householdId, type, scoped)) as unknown as Synced<T>[];
      return type === 'transaction' ? rows.map(shownName) : rows;
    },
    enabled: householdId !== null,
  });
}

/**
 * A bank name as it is shown: without the payment reference the bank put after a sender ("K Stankowska
 * 600000001844463762"; the owner, 01.10.2026: „nadal widzę ten numer”). The bank import drops it now, but names read
 * before stay stored with it until the next bank read — and older ones for good — so the screens drop it on reading.
 * The stored name is untouched.
 */
function shownName<T>(row: T): T {
  const name = (row as { merchantName?: string | null }).merchantName;
  if (!name) return row;
  const shown = withoutReference(name);
  return shown && shown !== name ? { ...row, merchantName: shown } : row;
}

/** The household's start day, read through the same cached query as useEntities('household'). */
function useCountFrom(): string | null {
  const { engine, householdId } = useSession();
  const { data } = useQuery({
    queryKey: ['entities', 'household', householdId, {}],
    queryFn: async () => (householdId ? ((await engine.list(householdId, 'household', {})) as unknown as Synced<Household>[]) : []),
    enabled: householdId !== null,
  });
  return data?.find((h) => h.id === householdId)?.countFrom ?? null;
}

export function useHousehold(): Synced<Household> | null {
  const { householdId } = useSession();
  const { data } = useEntities<Household>('household');
  return data?.find((h) => h.id === householdId) ?? null;
}

export function usePersons(includeArchived = false): Synced<Person>[] {
  const { data } = useEntities<Person>('person');
  return useMemo(
    () => (data ?? []).filter((p) => includeArchived || p.archivedAt === null).sort((a, b) => a.sortOrder - b.sortOrder),
    [data, includeArchived],
  );
}

export function useAccounts(includeArchived = false): Synced<Account>[] {
  const { data } = useEntities<Account>('account');
  return useMemo(
    () => (data ?? []).filter((a) => includeArchived || a.archivedAt === null).sort((a, b) => a.sortOrder - b.sortOrder),
    [data, includeArchived],
  );
}

/**
 * Every category, hidden ones included: a transaction keeps showing the category it has. Lists to
 * choose from leave the hidden ones out (`chooseable`).
 */
export function useCategories(): Synced<Category>[] {
  const { data } = useEntities<Category>('category');
  const groups = useEntities<CategoryGroup>('categoryGroup');
  // In the colour to show: a group's subcategories as shades of it (app/category-colors.ts).
  return useMemo(
    () => withGroupShades([...(data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder), liveGroups(groups.data)),
    [data, groups.data],
  );
}

/** Groups of categories (D-035: "Auto", "Rachunki"), in their order; hidden ones left out. */
export function useCategoryGroups(): Synced<CategoryGroup>[] {
  const { data } = useEntities<CategoryGroup>('categoryGroup');
  const categories = useEntities<Category>('category');
  // A group without a colour of its own shows its first subcategory's.
  return useMemo(() => withGroupColors(liveGroups(data), categories.data ?? []), [data, categories.data]);
}

function liveGroups(groups: readonly Synced<CategoryGroup>[] | undefined): Synced<CategoryGroup>[] {
  return [...(groups ?? [])].filter((g) => g.archivedAt === null).sort((a, b) => a.sortOrder - b.sortOrder || (a.name ?? '').localeCompare(b.name ?? ''));
}

/** The group a category belongs to, while that group is still there. */
export function groupOf<G extends CategoryGroup>(category: Pick<Category, 'groupId'> | null | undefined, groups: readonly G[]): G | null {
  return category?.groupId ? (groups.find((g) => g.id === category.groupId) ?? null) : null;
}

/** Categories to pick from: not hidden, plus the one already chosen even if it is hidden now. */
export function chooseable<C extends Category>(categories: readonly C[], chosen?: string | null): C[] {
  return categories.filter((c) => c.archivedAt === null || c.id === chosen);
}

/**
 * The expense type a transaction in this category starts with (D-037): the category's default, for
 * dates from this month on — earlier ones keep what they had.
 */
export function categoryExpenseType(categories: readonly Pick<Category, 'id' | 'defaultExpenseType'>[], categoryId: string | null | undefined, bookingDate: string): 'SHARED' | 'PERSONAL' | null {
  if (!categoryId || bookingDate < `${today().slice(0, 8)}01`) return null;
  return categories.find((c) => c.id === categoryId)?.defaultExpenseType ?? null;
}

/** Today in the device's time zone as a calendar date. */
export const today = () => todayIn();

/** Latest exchange rates into the base currency (for net worth); cached for offline use. */
function useRates(baseCurrency: string | undefined) {
  return useQuery({
    queryKey: ['rates', baseCurrency],
    enabled: Boolean(baseCurrency),
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<ExchangeRate[]> => {
      const cacheKey = `fd.rates.${baseCurrency}`;
      try {
        const { rates } = await callServer<{ rates: ExchangeRate[] }>('/rates', { query: { base: baseCurrency! } });
        try {
          localStorage.setItem(cacheKey, JSON.stringify(rates));
        } catch {
          // no storage
        }
        return rates;
      } catch {
        try {
          return JSON.parse(localStorage.getItem(cacheKey) ?? '[]') as ExchangeRate[];
        } catch {
          return [];
        }
      }
    },
  });
}

function rateLookup(rates: readonly ExchangeRate[]) {
  return (from: string, to: string): ExchangeRate | null =>
    rates.find((r) => (r.base === from && r.quote === to) || (r.base === to && r.quote === from)) ?? null;
}

export type DashboardData = {
  household: Household;
  period: Period;
  current: CashFlowSummary;
  previous: CashFlowSummary;
  /** The period for the cash-flow diagram: payments still pending at the bank count as made. */
  flow: CashFlowSummary;
  netWorth: NetWorth;
  /** Each person who owns a counted account: their accounts, joint ones split equally (D-027). */
  netWorthByPerson: { personId: string; worth: NetWorth }[];
  /** Accounts nobody owns; null when every counted account has an owner. */
  netWorthUnassigned: NetWorth | null;
  savingsValueMinor: bigint;
  cashMinor: bigint;
  recent: Synced<Transaction>[];
  settlement: SettlementResult;
  persons: Synced<Person>[];
  accounts: Synced<Account>[];
  categories: Synced<Category>[];
  /** Last six months up to the current period, oldest first (KPI sparklines). */
  trend: { income: bigint[]; spending: bigint[]; saved: bigint[]; cash: bigint[] };
};

/** Everything the dashboard shows, computed locally from the cache with the shared engines. */
export function useDashboard(kind: Period['kind'], anchor: string) {
  const { householdId } = useSession();
  const households = useEntities<Household>('household');
  const people = useEntities<Person>('person');
  const accountQuery = useEntities<Account>('account');
  const categoryQuery = useEntities<Category>('category');
  const household = households.data?.find((h) => h.id === householdId) ?? null;
  const persons = useMemo(() => [...(people.data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder), [people.data]);
  const accounts = useMemo(() => [...(accountQuery.data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder), [accountQuery.data]);
  const categories = useCategories();
  const transactions = useEntities<Transaction>('transaction');
  const rules = useEntities<SettlementRule>('settlementRule');
  const incomes = useEntities<IncomePeriod>('incomePeriod');
  const settlements = useEntities<Settlement>('settlement');
  const rates = useRates(household?.baseCurrency);
  const sources = [households, people, accountQuery, categoryQuery, transactions, rules, incomes, settlements];
  const ready = sources.every((query) => query.data !== undefined);
  const error = sources.some((query) => query.isError) || (ready && !household);

  const data = useMemo<DashboardData | null>(() => {
    if (!ready || !household || !transactions.data || !rules.data || !incomes.data || !settlements.data) return null;
    const period = kind === 'CUSTOM' ? periodContaining('MONTH', anchor) : periodContaining(kind, anchor);
    const withInterest = withLoanInterest(accounts, transactions.data, today());
    const accountMap = new Map(withInterest.map((a) => [a.id, toLedgerAccount(a)]));
    const ledger = transactions.data.map(toLedgerTransaction);
    const current = summarizeCashFlow(ledger, accountMap, period, household.baseCurrency);
    const previous = summarizeCashFlow(ledger, accountMap, previousPeriod(period), household.baseCurrency);
    // The diagram draws a payment still pending at the bank in its category (the owner, 30.09.2026:
    // "unbooked payments are to be in their category all the same"), not among other outflows.
    const flow = summarizeCashFlow(
      ledger.map((t) => (t.status === 'PENDING' ? { ...t, status: 'BOOKED' as const } : t)),
      accountMap,
      period,
      household.baseCurrency,
    );
    const lookup = rateLookup(rates.data ?? []);
    const netWorth = computeNetWorth([...accountMap.values()], household.baseCurrency, lookup);
    const ownersOf = new Map(withInterest.map((a) => [a.id, a.owners]));
    const counted = new Set(netWorth.accounts.map((v) => v.accountId));
    const worthOf = (who: string) => computeNetWorth([...accountMap.values()], household.baseCurrency, lookup, partOf((id) => ownersOf.get(id), who));
    const netWorthByPerson = persons
      .filter((p) => withInterest.some((a) => counted.has(a.id) && a.owners.includes(p.id)))
      .map((p) => ({ personId: p.id, worth: worthOf(p.id) }));
    const unassigned = worthOf(UNASSIGNED);
    const netWorthUnassigned = unassigned.accounts.length > 0 ? unassigned : null;
    const savingsValueMinor = netWorth.accounts
      .filter((v) => accountMap.get(v.accountId)?.isSavingsAsset)
      .reduce((sum, v) => sum + v.baseMinor, 0n);
    const cashMinor = netWorth.accounts
      .filter((v) => {
        const a = accountMap.get(v.accountId);
        return a && !a.isSavingsAsset && (a.type === 'CURRENT' || a.type === 'CASH');
      })
      .reduce((sum, v) => sum + v.baseMinor, 0n);
    const settlement = computeSettlement(
      transactions.data.map(toEngineTransaction),
      settlements.data.map(toSettlementRecord),
      {
        persons: persons.map((p) => ({ id: p.id, sortOrder: p.sortOrder })),
        accounts: new Map(accounts.map((a) => [a.id, toAccountInfo(a)])),
        rules: rules.data.map(toEngineRule),
        incomes: incomes.data.map(toEngineIncome),
        baseCurrency: household.baseCurrency,
        transactionsById: new Map(transactions.data.map((t) => [t.id, toEngineTransaction(t)])),
      },
    );
    // A new value for a car or a house is not a transaction to show among the latest.
    const recent = [...transactions.data.filter((t) => t.kind !== 'BALANCE_ADJUSTMENT')]
      .sort((a, b) => (a.bookingDate !== b.bookingDate ? (a.bookingDate < b.bookingDate ? 1 : -1) : a.id < b.id ? 1 : -1))
      .slice(0, 8);
    // Six month-ends for the sparklines; the cash series walks back from the current balances.
    const monthEnds = Array.from({ length: 6 }, (_, i) => endOfMonth(addMonths(period.end, i - 5)));
    const months = monthEnds.map((end) => summarizeCashFlow(ledger, accountMap, periodContaining('MONTH', end), household.baseCurrency).booked);
    const cashAccounts = [...accountMap.values()].filter((a) => !a.isSavingsAsset && (a.type === 'CURRENT' || a.type === 'CASH'));
    const cash = monthEnds.map((end) =>
      cashAccounts.reduce((sum, account) => {
        const later = ledger
          .filter((t) => t.accountId === account.id && t.bookingDate > end && t.status !== 'REVERSED' && t.status !== 'CANCELLED')
          .reduce((acc, t) => acc + t.amountMinor, 0n);
        return sum + (toBase(account.balanceMinor - later, account.currency, household.baseCurrency, lookup) ?? 0n);
      }, 0n),
    );
    const trend = {
      income: months.map((m) => m.incomeMinor),
      spending: months.map((m) => m.netSpendMinor),
      saved: months.map((m) => m.savingsContributionMinor),
      cash,
    };
    return { household, period, current, previous, flow, netWorth, netWorthByPerson, netWorthUnassigned, savingsValueMinor, cashMinor, recent, settlement, persons, accounts: withInterest, categories, trend };
  }, [ready, household, persons, accounts, categories, transactions.data, rules.data, incomes.data, settlements.data, rates.data, kind, anchor]);

  return {
    data,
    loading: data === null && !error,
    error,
    retrying: sources.some((query) => query.isFetching),
    retry: async () => { await Promise.all(sources.map((query) => query.refetch())); },
  };
}

/** Everything the settlement engine needs, built from the local cache (shared by several screens). */
export function useSettlementContext(): { context: SettlementContext; settlements: Synced<Settlement>[]; transactions: Synced<Transaction>[] } | null {
  const household = useHousehold();
  const persons = usePersons(true);
  const accounts = useAccounts(true);
  const transactions = useEntities<Transaction>('transaction');
  const rules = useEntities<SettlementRule>('settlementRule');
  const incomes = useEntities<IncomePeriod>('incomePeriod');
  const settlements = useEntities<Settlement>('settlement');
  return useMemo(() => {
    if (!household || !transactions.data || !rules.data || !incomes.data || !settlements.data) return null;
    return {
      context: {
        persons: persons.map((p) => ({ id: p.id, sortOrder: p.sortOrder })),
        accounts: new Map(accounts.map((a) => [a.id, toAccountInfo(a)])),
        rules: rules.data.map(toEngineRule),
        incomes: incomes.data.map(toEngineIncome),
        baseCurrency: household.baseCurrency,
        transactionsById: new Map(transactions.data.map((t) => [t.id, toEngineTransaction(t)])),
      },
      settlements: settlements.data,
      transactions: transactions.data,
    };
  }, [household, persons, accounts, transactions.data, rules.data, incomes.data, settlements.data]);
}

// ------------------------------------------------------------------------------ loans

const DEBT_TYPES = new Set<Account['type']>(['LOAN', 'MORTGAGE']);

/** Repayments into a loan account (own transfers arriving on it). */
function repaymentsOf(account: Account, transactions: readonly Transaction[]) {
  return transactions
    .filter((t) => t.accountId === account.id && t.deletedAt === null && t.status === 'BOOKED' && BigInt(t.amountMinor) > 0n)
    .map((t) => ({ date: t.bookingDate, amountMinor: BigInt(t.amountMinor) }));
}

/** A loan whose balance comes from its terms: the agreement's schedule (D-029) or a known rate (D-023). */
function computedLoan(account: Account): boolean {
  // Records cached before the loan fields existed read as undefined.
  if (!DEBT_TYPES.has(account.type)) return false;
  return (account.interestRateBasisPoints ?? null) !== null || ((account.paymentCount ?? null) !== null && (account.firstPaymentDate ?? null) !== null);
}

/** Such loans show the balance from their terms everywhere, net worth included. */
function withLoanInterest<A extends Account>(accounts: readonly A[], transactions: readonly Transaction[], on: string): A[] {
  return accounts.map((account) => {
    if (!computedLoan(account)) return account;
    const status = loanStatus(toLoanTerms(account), repaymentsOf(account, transactions), on);
    return { ...account, balanceMinor: String(-status.outstandingMinor) };
  });
}

/** From when an account is in the records: a scheduled loan from the day it began. */
function historyStart(account: Account): string {
  const start = DEBT_TYPES.has(account.type) ? loanStartDate(toLoanTerms(account)) : null;
  return start && start < account.openingBalanceDate ? start : account.openingBalanceDate;
}

export type LoanView = { account: Synced<Account>; status: LoanStatus };

export function useLoans(): LoanView[] {
  const accounts = useAccounts();
  const transactions = useEntities<Transaction>('transaction');
  return useMemo(
    () =>
      accounts
        .filter((a) => DEBT_TYPES.has(a.type))
        .map((account) => ({ account, status: loanStatus(toLoanTerms(account), repaymentsOf(account, transactions.data ?? []), today()) })),
    [accounts, transactions.data],
  );
}

// ------------------------------------------------------------------------------ recurring

export type RecurringItem = RecurringPattern & {
  decision: Synced<RecurringDecision> | null;
  status: RecurringDecision['status'];
  displayName: string;
  shownKind: RecurringKind;
  /** The loan account this payment repays, when known. */
  linkedAccountId: string | null;
};

/**
 * Recurring payments found in the local cache (D-022), merged with the owner's decisions.
 * Several decisions for one signature (two devices offline) resolve to the newest.
 */
export function useRecurring() {
  // The whole history, before the start day too: a monthly payment shows itself only over months.
  const transactions = useEntities<Transaction>('transaction', { history: true });
  const decisions = useEntities<RecurringDecision>('recurringPattern');
  const accountQuery = useEntities<Account>('account');
  const items = useMemo(
    () => (transactions.data ? recurringItems(transactions.data, decisions.data, accountQuery.data, today()) : []),
    [transactions.data, decisions.data, accountQuery.data],
  );
  const sources = [transactions, decisions, accountQuery];
  return {
    items,
    loading: sources.some(query => query.isPending),
    error: sources.some(query => query.isError),
    retrying: sources.some(query => query.isFetching),
    retry: async () => { await Promise.all(sources.map(query => query.refetch())); },
  };
}

/**
 * The recurring payments of the local cache with the owner's decisions. Several cards of a screen
 * ask at once (the overview: upcoming payments, figures) with the very same query data,
 * so the last answer is kept and handed out again while its inputs stay the same objects: the whole
 * history is searched once per change instead of once per card.
 */
let lastRecurring: { key: readonly unknown[]; items: RecurringItem[] } | null = null;

function recurringItems(
  transactions: readonly Synced<Transaction>[],
  decisions: readonly Synced<RecurringDecision>[] | undefined,
  accountList: readonly Synced<Account>[] | undefined,
  on: string,
): RecurringItem[] {
  const key = [transactions, decisions, accountList, on];
  if (lastRecurring && lastRecurring.key.every((part, i) => part === key[i])) return lastRecurring.items;
  const accounts = accountList ?? [];
  const bySignature = new Map<string, Synced<RecurringDecision>>();
  for (const decision of decisions ?? []) {
    const existing = bySignature.get(decision.signature);
    if (!existing || existing.updatedAt < decision.updatedAt) bySignature.set(decision.signature, decision);
  }
  const patterns = detectRecurring(
    transactions.filter((t) => t.deletedAt === null).map(toRecurringTransaction),
    { today: on, accountTypes: new Map(accounts.map((a) => [a.id, a.type])) },
  );
  const items = patterns.map((pattern): RecurringItem => {
    const decision = bySignature.get(pattern.signature) ?? null;
    const linked = decision?.linkedAccountId ?? pattern.counterpartAccountId;
    return {
      ...pattern,
      decision,
      status: decision?.status ?? 'SUGGESTED',
      displayName: decision?.name ?? pattern.name,
      // The kind the owner chose stays; one only recorded at confirmation gives way when it
      // is "other" and the detection now says better (water became a bill).
      shownKind: decision && ((decision.kindChosen ?? false) || decision.kind !== 'OTHER') ? decision.kind : pattern.kind,
      linkedAccountId: linked && accounts.some((a) => a.id === linked && DEBT_TYPES.has(a.type)) ? linked : null,
    };
  });
  lastRecurring = { key, items };
  return items;
}

/**
 * Recurring payments the bank took off their due day — a weekend or a bank holiday moved them,
 * sometimes into the next month — by transaction id. A dismissed pattern marks nothing.
 */
export function useBankDayShifts(): ReadonlyMap<string, BankDayShift> {
  const { items } = useRecurring();
  return useMemo(() => {
    const shifts = new Map<string, BankDayShift>();
    for (const item of items) {
      if (item.status === 'DISMISSED') continue;
      for (const occurrence of item.occurrences) {
        const shift = bankDayShift(item, occurrence.date);
        if (shift) shifts.set(occurrence.id, shift);
      }
    }
    return shifts;
  }, [items]);
}

/**
 * What these accounts spend on an ordinary day (last 90 days), outside the recurring payments
 * the forecasts put on their own dates. A dismissed pattern is ordinary spending again.
 */
export function useEverydaySpending(accountIds: readonly string[], currency: string, recurring: readonly RecurringItem[]) {
  const transactions = useEntities<Transaction>('transaction');
  const key = accountIds.join('|');
  return useMemo(() => {
    if (!transactions.data) return 0n;
    const recurringIds = new Set(recurring.filter((p) => p.status !== 'DISMISSED').flatMap((p) => p.transactionIds));
    return averageDailySpending(transactions.data.filter((t) => t.deletedAt === null).map(toRecurringTransaction), {
      accountIds: new Set(key.split('|')),
      currency,
      today: today(),
      recurringIds,
    });
  }, [transactions.data, key, currency, recurring]);
}

// ------------------------------------------------------------------------------ net worth history

export type NetWorthRange = '1M' | '3M' | '6M' | 'YTD' | '1Y' | '5Y' | 'ALL';
const RANGE_MONTHS: Record<Exclude<NetWorthRange, 'ALL' | 'YTD'>, number> = { '1M': 1, '3M': 3, '6M': 6, '1Y': 12, '5Y': 60 };

/** Net worth over time, sampled so that any range has at most ~120 points. */
export function useNetWorthHistory(range: NetWorthRange, who: string | null = null, comparisonPeriod?: Pick<Period, 'start' | 'end'>): { points: NetWorthPoint[]; currency: string; periodChange: bigint | null } | null {
  const household = useHousehold();
  const accounts = useAccounts(true);
  const transactions = useEntities<Transaction>('transaction');
  const investmentEvents = useEntities<InvestmentEvent>('investmentEvent');
  const rates = useRates(household?.baseCurrency);
  return useMemo(() => {
    if (!household || !transactions.data || accounts.length === 0) return null;
    const live = accounts.filter((a) => !a.archivedAt);
    // An investment account connected today still has the money paid in since it opened
    // (Trading 212 reports every deposit): it counts from its first one.
    const moves = new Map<string, { type: string; date: string; amountMinor: bigint }[]>();
    for (const e of investmentEvents.data ?? []) {
      if (e.deletedAt !== null) continue;
      const move = { type: e.type, date: e.bookingDate, amountMinor: BigInt(e.amountMinor) };
      const list = moves.get(e.accountId);
      if (list) list.push(move);
      else moves.set(e.accountId, [move]);
    }
    const since = (a: Account) => {
      const first = (moves.get(a.id) ?? []).reduce<string | null>((min, m) => (min === null || m.date < min ? m.date : min), null);
      return first && first < historyStart(a) ? first : historyStart(a);
    };
    const end = today();
    // The chart starts where the records do; a loan known from its schedule goes back further,
    // but a day with the debt and none of the money would be a false low.
    const opened = live.reduce((min, a) => (a.openingBalanceDate < min ? a.openingBalanceDate : min), end);
    // Nor before the household's start day: without the transactions before it, the line would
    // run flat there, as if nothing had happened (D-040).
    const earliest = household.countFrom && household.countFrom > opened && household.countFrom < end ? household.countFrom : opened;
    const wanted = range === 'ALL' ? earliest : range === 'YTD' ? `${end.slice(0, 4)}-01-01` : addMonths(end, -RANGE_MONTHS[range]);
    const start = wanted < earliest ? earliest : wanted;
    const plan = netWorthDatePlan(start, end, earliest, comparisonPeriod);
    const byId = new Map(live.map((a) => [a.id, a]));
    // What the owner values by hand (a car): its corrections, to draw the value between them.
    const valued = new Map<string, { date: string; amountMinor: bigint }[]>();
    for (const t of transactions.data) {
      const account = byId.get(t.accountId);
      if (!account || account.type !== 'ASSET' || account.provider !== 'MANUAL' || t.deletedAt !== null || t.status === 'REVERSED' || t.status === 'CANCELLED') continue;
      const change = { date: t.bookingDate, amountMinor: BigInt(t.amountMinor) };
      const list = valued.get(t.accountId);
      if (list) list.push(change);
      else valued.set(t.accountId, [change]);
    }
    // A loan's balance is asked for every day of the chart: its schedule and repayments once per loan.
    const loanBalance = new Map<string, (date: string) => bigint>();
    const loanOn = (full: Account): ((date: string) => bigint) => {
      let balance = loanBalance.get(full.id);
      if (!balance) {
        const terms = toLoanTerms(full);
        const scheduled = scheduledLoanBalances(terms);
        const repayments = repaymentsOf(full, transactions.data ?? []);
        balance = (date) => {
          const owed = scheduled(date);
          return owed !== null ? -owed : -loanStatus(terms, repayments, date).outstandingMinor;
        };
        loanBalance.set(full.id, balance);
      }
      return balance;
    };
    const allPoints = netWorthHistory(
      live.map((a) => ({ ...toLedgerAccount(a), openingDate: since(a) })),
      transactions.data.filter((t) => t.deletedAt === null).map(toLedgerTransaction),
      plan.dates,
      household.baseCurrency,
      rateLookup(rates.data ?? []),
      (account, date) => {
        const full = byId.get(account.id);
        if (full?.type === 'ASSET' && full.provider === 'MANUAL') {
          return valuedAssetOn(full.openingBalanceDate, BigInt(full.openingBalanceMinor), valued.get(full.id) ?? [], date);
        }
        const cash = full ? moves.get(full.id) : undefined;
        if (full && cash && date < full.openingBalanceDate) return investmentValueOn(BigInt(full.balanceMinor), cash, date);
        if (!full || !computedLoan(full)) return null;
        return loanOn(full)(date);
      },
      who === null ? undefined : partOf((id) => byId.get(id)?.owners, who),
    );
    const chartDates = new Set(plan.chartDates);
    const points = allPoints.filter((p) => chartDates.has(p.date));
    const before = plan.comparison ? allPoints.find((p) => p.date === plan.comparison!.start)?.netMinor : undefined;
    const after = plan.comparison ? allPoints.find((p) => p.date === plan.comparison!.end)?.netMinor : undefined;
    const periodChange = before !== undefined && after !== undefined ? after - before : null;
    return { points, currency: household.baseCurrency, periodChange };
  }, [household, accounts, transactions.data, investmentEvents.data, rates.data, range, who, comparisonPeriod?.start, comparisonPeriod?.end]);
}

// ------------------------------------------------------------------------------ learned categories

/** Merchant → category learned from the household's own categorised transactions. */
export function useCategoryMemory(): { memory: Map<string, CategorySuggestion>; transactions: Synced<Transaction>[] } {
  const transactions = useEntities<Transaction>('transaction');
  return useMemo(() => {
    const live = (transactions.data ?? []).filter((t) => t.deletedAt === null);
    return { memory: learnCategories(live.map(toCategorized)), transactions: live };
  }, [transactions.data]);
}

// ------------------------------------------------------------------------------ daily spending

export type DaySpending = { date: string; spentMinor: bigint; count: number };

/** Net spending per day in the base currency (expenses minus refunds), for the heatmap. */
export function useDailySpending(from: string, to: string): Map<string, DaySpending> | null {
  const household = useHousehold();
  const accounts = useAccounts(true);
  const transactions = useEntities<Transaction>('transaction');
  return useMemo(() => {
    if (!household || !transactions.data) return null;
    const accountMap = new Map(accounts.map((a) => [a.id, toLedgerAccount(a)]));
    const days = new Map<string, DaySpending>();
    for (const t of transactions.data) {
      if (t.deletedAt !== null || t.status !== 'BOOKED' || t.bookingDate < from || t.bookingDate > to) continue;
      const ledger = toLedgerTransaction(t);
      const flow = classifyTransaction(ledger, accountMap);
      if (flow !== 'EXPENSE' && flow !== 'REFUND') continue;
      const amount = baseAmount(ledger, household.baseCurrency);
      if (amount === null) continue;
      const magnitude = amount < 0n ? -amount : amount;
      const day = days.get(t.bookingDate) ?? { date: t.bookingDate, spentMinor: 0n, count: 0 };
      day.spentMinor += flow === 'EXPENSE' ? magnitude : -magnitude;
      if (flow === 'EXPENSE') day.count += 1;
      days.set(t.bookingDate, day);
    }
    return days;
  }, [household, accounts, transactions.data, from, to]);
}

// ------------------------------------------------------------------------------ budgets

export type BudgetView = {
  budget: Synced<Budget>;
  /** Null (and no group): all spending. */
  category: Synced<Category> | null;
  /** A budget for a whole group of categories (D-035). */
  group: Synced<CategoryGroup> | null;
  status: BudgetStatus;
  /** Spending in each of the last six months, oldest first, for the bars under the budget. */
  history: { month: string; spentMinor: bigint }[];
};

export type BudgetsData = {
  month: string;
  currency: string;
  views: BudgetView[];
  /** Spending per category (null: none) and month, and all spending per month. */
  byCategory: Map<string | null, Map<string, bigint>>;
  /** Spending per group of categories and month. */
  byGroup: Map<string, Map<string, bigint>>;
  total: Map<string, bigint>;
  /** Income per category in the month itself (null: none). */
  incomeByCategory: Map<string | null, bigint>;
};

/** Which budget a row is: all spending "*", a group "g:<id>", or a category's id. */
const budgetKey = (budget: Pick<Budget, 'categoryId' | 'categoryGroupId'>) =>
  budget.categoryGroupId ? `g:${budget.categoryGroupId}` : (budget.categoryId ?? '*');

/**
 * Budgets for the month of `anchor` (D-032): each live budget with what was spent, its pace and
 * the months before it; one budget per category — two made offline on two devices resolve to the
 * newest. Spending is booked net spending in the base currency, as on the dashboard.
 */
export function useBudgets(anchor: string): BudgetsData | null {
  const household = useHousehold();
  const accounts = useAccounts(true);
  const categories = useCategories();
  const groups = useCategoryGroups();
  const transactions = useEntities<Transaction>('transaction');
  const budgets = useEntities<Budget>('budget');
  return useMemo(() => {
    if (!household || !transactions.data) return null;
    const month = startOfMonthOf(anchor);
    const live = new Map<string, Synced<Budget>>();
    for (const b of budgets.data ?? []) {
      if (b.deletedAt !== null || b.archivedAt !== null) continue;
      const key = budgetKey(b);
      const existing = live.get(key);
      if (!existing || existing.updatedAt < b.updatedAt) live.set(key, b);
    }
    const earliest = [...live.values()].reduce((min, b) => (b.effectiveFrom < min ? b.effectiveFrom : min), addMonths(month, -11));
    // A payment still pending at the bank counts as made, as in the cash-flow diagram (the owner,
    // 30.09.2026): the budget and the diagram must show the same spending, not two different sums.
    const ledger = transactions.data
      .filter((t) => t.deletedAt === null)
      .map(toLedgerTransaction)
      .map((t) => (t.status === 'PENDING' ? { ...t, status: 'BOOKED' as const } : t));
    const accountMap = new Map(accounts.map((a) => [a.id, toLedgerAccount(a)]));
    const byCategory = new Map<string | null, Map<string, bigint>>();
    const byGroup = new Map<string, Map<string, bigint>>();
    const groupOfCategory = new Map(categories.flatMap((c) => (groupOf(c, groups) ? [[c.id, c.groupId!] as const] : [])));
    const total = new Map<string, bigint>();
    const incomeByCategory = new Map<string | null, bigint>();
    for (const m of monthsFromTo(earliest, month)) {
      const summary = summarizeCashFlow(ledger, accountMap, periodContaining('MONTH', m), household.baseCurrency);
      total.set(m, summary.booked.netSpendMinor);
      if (m === month) for (const c of summary.incomeByCategory) incomeByCategory.set(c.categoryId, c.incomeMinor);
      for (const c of summary.byCategory) {
        const series = byCategory.get(c.categoryId) ?? new Map<string, bigint>();
        series.set(m, c.netSpendMinor);
        byCategory.set(c.categoryId, series);
        const groupId = c.categoryId ? groupOfCategory.get(c.categoryId) : undefined;
        if (groupId) {
          const grouped = byGroup.get(groupId) ?? new Map<string, bigint>();
          grouped.set(m, (grouped.get(m) ?? 0n) + c.netSpendMinor);
          byGroup.set(groupId, grouped);
        }
      }
    }
    const now = today();
    const views = [...live.values()].map((budget): BudgetView => {
      const spending = budget.categoryGroupId
        ? (byGroup.get(budget.categoryGroupId) ?? new Map<string, bigint>())
        : budget.categoryId === null
          ? total
          : (byCategory.get(budget.categoryId) ?? new Map<string, bigint>());
      const definition = { amountMinor: BigInt(budget.amountMinor), rollover: budget.rollover, effectiveFrom: budget.effectiveFrom, monthMask: budget.monthMask ?? null };
      return {
        budget,
        category: budget.categoryId ? (categories.find((c) => c.id === budget.categoryId) ?? null) : null,
        group: budget.categoryGroupId ? (groups.find((g) => g.id === budget.categoryGroupId) ?? null) : null,
        status: budgetStatus(definition, spending, month, now),
        history: monthsFromTo(addMonths(month, -5), month).map((m) => ({ month: m, spentMinor: spending.get(m) ?? 0n })),
      };
    });
    // All spending first, then the categories by how much of their budget is used.
    const everything = (v: BudgetView) => v.category === null && v.group === null;
    views.sort((a, b) => (everything(a) ? -1 : everything(b) ? 1 : b.status.usedBasisPoints - a.status.usedBasisPoints));
    return { month, currency: household.baseCurrency, views, byCategory, byGroup, total, incomeByCategory };
  }, [household, accounts, categories, groups, transactions.data, budgets.data, anchor]);
}

const startOfMonthOf = (date: string) => `${date.slice(0, 8)}01`;
